<script lang="ts">
  import { Collapsible } from 'bits-ui';
  import type { Snippet } from 'svelte';
  import ThemeIcon from '../themes/ThemeIcon.svelte';
  import type { IconName } from '../themes/contract';

  /**
   * A named toggle over a stable region. The default face is a ghost button with a label; `trigger`
   * instead turns supplied content (a standings row) into the toggle, and its text names it.
   */
  type Face =
    | { label: string; expandedLabel?: string; icon?: IconName; full?: boolean; trigger?: never }
    | { trigger: Snippet; label?: never; expandedLabel?: never; icon?: never; full?: never };

  let {
    open = $bindable(false),
    id,
    onOpen = () => {},
    children,
    ...face
  }: Face & {
    open?: boolean;
    id: string;
    onOpen?: () => void;
    children?: Snippet;
  } = $props();

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) onOpen();
  }
</script>

<Collapsible.Root bind:open onOpenChange={handleOpenChange}>
  {#if face.trigger}
    <Collapsible.Trigger class="disclosure-row" aria-controls={id}>
      {@render face.trigger()}
    </Collapsible.Trigger>
  {:else}
    <Collapsible.Trigger class={`btn ghost${face.full ? ' full' : ''}`} aria-controls={id}>
      {#if face.icon}<ThemeIcon name={face.icon} />{/if}{open
        ? (face.expandedLabel ?? face.label)
        : face.label}
    </Collapsible.Trigger>
  {/if}
  <Collapsible.Content {id} class="disclosure-content">
    {#if open}
      {@render children?.()}
    {/if}
  </Collapsible.Content>
</Collapsible.Root>

<style>
  :global(.disclosure-content:not([hidden])) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
  }
  /* The supplied content keeps its own layout; the button adds only the toggle behaviour. */
  :global(.disclosure-row) {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 44px;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: inherit;
    cursor: pointer;
  }
</style>
